import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react';
import { portfolioConfig } from '@/config';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { cn } from '@/lib/utils';
import { useCollection, useConfig } from '@/lib/siteData';

interface ProjectsSectionConfig {
  heading?: string;
  body?: string;
  visible?: boolean;
}
const defaultProjectsSection: ProjectsSectionConfig = {};

const AUTOPLAY_MS = 5500;

interface ProjectRow {
  slug: string;
  title: string;
  tag: string;
  outcome: string;
  associatedWith?: string | null;
  description: string;
  image?: string | null;
  link?: string | null;
  sortOrder?: number;
  visible?: boolean;
}

// Cap the homepage carousel — `Selected Work` should feel curated.
// Full list lives on /portfolio. Sortable via sortOrder in /admin/projects.
const HOME_CAROUSEL_LIMIT = 8;

export function Projects({ index }: { index?: number } = {}) {
  const portfolioSection = useConfig<ProjectsSectionConfig>('projects_section', defaultProjectsSection);
  const { data: projectsFromApi } = useCollection<ProjectRow>('projects');
  const allProjects = projectsFromApi.length > 0
    ? projectsFromApi.filter((p) => p.visible !== false)
    : portfolioConfig.projects;
  const projects = allProjects.slice(0, HOME_CAROUSEL_LIMIT);
  const totalProjectsCount = allProjects.length;
  const prefersReducedMotion = useReducedMotion();
  const trackRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [userPaused, setUserPaused] = useState(false);

  const scrollToIndex = useCallback(
    (index: number, behavior: ScrollBehavior = 'smooth') => {
      const track = trackRef.current;
      if (!track) return;
      const card = track.children[index] as HTMLElement | undefined;
      if (!card) return;
      const left = card.offsetLeft - track.offsetLeft;
      track.scrollTo({ left, behavior: prefersReducedMotion ? 'auto' : behavior });
    },
    [prefersReducedMotion]
  );

  const goTo = useCallback(
    (index: number) => {
      if (projects.length === 0) return;
      const normalized = (index + projects.length) % projects.length;
      setActiveIndex(normalized);
      scrollToIndex(normalized);
    },
    [projects.length, scrollToIndex]
  );

  // Track scroll position → derive active index
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const children = Array.from(track.children) as HTMLElement[];
        if (children.length === 0) return;
        const center = track.scrollLeft + track.clientWidth / 2;
        let nearest = 0;
        let nearestDist = Infinity;
        children.forEach((child, i) => {
          const childCenter = child.offsetLeft + child.clientWidth / 2 - track.offsetLeft;
          const dist = Math.abs(childCenter - center);
          if (dist < nearestDist) {
            nearestDist = dist;
            nearest = i;
          }
        });
        setActiveIndex(nearest);
      });
    };
    track.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      track.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  // Autoplay
  useEffect(() => {
    if (prefersReducedMotion || isPaused || userPaused || projects.length <= 1) return;
    const id = window.setInterval(() => {
      setActiveIndex((prev) => {
        const next = (prev + 1) % projects.length;
        scrollToIndex(next);
        return next;
      });
    }, AUTOPLAY_MS);
    return () => window.clearInterval(id);
  }, [prefersReducedMotion, isPaused, userPaused, projects.length, scrollToIndex]);

  if (portfolioSection.visible === false) return null;
  if (!projects || projects.length === 0) return null;

  // Dots: if too many, show condensed set (active + neighbours)
  const showAllDots = projects.length <= 12;

  return (
    <section
      id="projects"
      className="w-full py-16 md:py-20 bg-surface-gray border-t border-surface-border-gray"
    >
      <div className="w-full max-w-[1200px] mx-auto px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center mb-10 md:mb-12">
          <span className="font-mono-label text-xs uppercase tracking-[0.2em] text-text-light">
            {index != null && <span className="text-accent-blue mr-1">{String(index).padStart(2, '0')} — </span>}
            SELECTED WORK
          </span>
          <h2 className="font-geist-mono text-3xl md:text-[2.5rem] font-medium text-text-primary mt-3 leading-[1.1] tracking-tight">
            {portfolioSection.heading}
          </h2>
          {portfolioSection.body ? (
            <p className="font-body text-base text-text-gray mt-3 max-w-xl mx-auto whitespace-pre-line">
              {portfolioSection.body}
            </p>
          ) : (
            <p className="font-body text-base text-text-gray mt-3 max-w-xl mx-auto">
              {totalProjectsCount}+ verified projects spanning payments, mobile money, card issuing,
              govtech and core banking — each solving a fundamental infrastructure problem.
            </p>
          )}
          <Link
            to="/portfolio"
            className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-accent-blue hover:underline"
          >
            View the full portfolio
            <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        <div
          className="relative"
          role="region"
          aria-roledescription="carousel"
          aria-label="Projects"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          onFocus={() => setIsPaused(true)}
          onBlur={() => setIsPaused(false)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowRight') {
              e.preventDefault();
              goTo(activeIndex + 1);
            } else if (e.key === 'ArrowLeft') {
              e.preventDefault();
              goTo(activeIndex - 1);
            }
          }}
        >
          <div
            ref={trackRef}
            className="flex gap-5 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 -mx-6 px-6 lg:mx-0 lg:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {projects.map((p, idx) => (
              <article
                key={p.slug}
                // aria-hidden on inactive slides keeps the heading count down
                // for screen readers (only the active card's H3 is announced).
                aria-hidden={idx !== activeIndex}
                className="flex flex-col p-6 rounded-xl border border-surface-border-gray bg-surface-elevated snap-center shrink-0 w-[85%] sm:w-[60%] md:w-[calc((100%-1.25rem)/2)] lg:w-[calc((100%-2.5rem)/3)]"
                role="group"
                aria-roledescription="slide"
                aria-label={`${idx + 1} of ${projects.length}`}
              >
                <div className="mb-3 flex items-center justify-between gap-3">
                  <span className="inline-flex items-center rounded-full border border-accent-blue/30 bg-accent-blue/5 px-2.5 py-0.5 text-[10px] font-mono-label uppercase tracking-[0.15em] text-accent-blue">
                    {p.tag}
                  </span>
                  <span className="text-[10px] font-mono-label uppercase tracking-wider text-text-light whitespace-nowrap">
                    {p.outcome}
                  </span>
                </div>
                <h3 className="font-heading text-lg font-semibold text-text-primary leading-snug mb-2">
                  {p.title}
                </h3>
                {p.associatedWith && (
                  <p className="font-body text-xs text-text-light mb-3">{p.associatedWith}</p>
                )}
                <p className="font-body text-sm text-text-gray leading-relaxed flex-1">
                  {p.description}
                </p>
              </article>
            ))}
          </div>

          {/* Controls */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
            {!prefersReducedMotion && projects.length > 1 && (
              <button
                type="button"
                onClick={() => setUserPaused((p) => !p)}
                aria-pressed={userPaused}
                aria-label={userPaused ? 'Resume automatic rotation' : 'Pause automatic rotation'}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-surface-border-gray bg-surface-elevated text-text-gray transition-colors hover:border-accent-blue hover:text-accent-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-blue"
              >
                {userPaused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
              </button>
            )}
            <button
              type="button"
              onClick={() => goTo(activeIndex - 1)}
              aria-label="Previous project"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-surface-border-gray bg-surface-elevated text-text-gray transition-colors hover:border-accent-blue hover:text-accent-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-blue"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            {showAllDots ? (
              <div className="flex items-center gap-2" role="tablist" aria-label="Select project">
                {projects.map((_, idx) => (
                  <button
                    key={idx}
                    type="button"
                    role="tab"
                    aria-selected={idx === activeIndex}
                    aria-label={`Go to project ${idx + 1}`}
                    onClick={() => goTo(idx)}
                    className={cn(
                      'h-2 rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-blue',
                      idx === activeIndex
                        ? 'w-6 bg-accent-blue'
                        : 'w-2 bg-surface-border-gray hover:bg-text-light'
                    )}
                  />
                ))}
              </div>
            ) : (
              <div
                className="font-mono-label text-xs uppercase tracking-[0.2em] text-text-light tabular-nums"
                aria-live="polite"
              >
                {String(activeIndex + 1).padStart(2, '0')} / {projects.length}
              </div>
            )}

            <button
              type="button"
              onClick={() => goTo(activeIndex + 1)}
              aria-label="Next project"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-surface-border-gray bg-surface-elevated text-text-gray transition-colors hover:border-accent-blue hover:text-accent-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-blue"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          <div className="mt-8 flex justify-center">
            <Link
              to="/portfolio"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-accent-blue hover:text-accent-blue-hover transition-colors"
            >
              View the full portfolio
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
